import { HttpErrorResponse } from "@angular/common/http";

export interface ProblemMenuItem {
    menuHead: string;
    subMenus: ProblemMenuItem[];
    menuItem: ProblemMenuConfig | null;
    shouldExpandDropdown?: boolean;
    selected: boolean;
};

export interface ProblemMenuConfig {
    ProblemMenuConfig_RefId: number;
    ProblemType_RefId: number | null,
    Group_RefId: number | null;
    MenuOne: string;
    MenuTwo: string;
    MenuThree: string;
    messageDisplay?: string;
    CanCreateCodeCase: boolean;
    AdditionalQuestions: QuestionsPayload[];
    IsSNation: boolean;
    IsTWNation: boolean;
};

export interface ProblemMenuSelection extends ProblemMenuConfig {
    selected: boolean;
};

export interface MessageBox {
    title: string,
    titleInfo: string,
    message: string
};

export interface ProblemMenuInputSuggestion {
    suggestionText: string;
    menuItem: ProblemMenuConfig;
};

export interface CheckOptionAnswers {
    option: string,
    answer: boolean
};

export interface HttpErrorPayload {
    error: string,
    headers: string,
    message: string,
    name: string,
    // ok: boolean,
    statusCode: number,
    statusText: string,
    URL: string
}

export interface ErrorExport {
    callerAction: string,
    callerMethod: string,
    // stackTrace: string,
    error: HttpErrorPayload
}

export interface QuestionAnswer {
    problemMenuConfig_RefId?: number;
    questionNumber: number;
    question: string;
    questionType: string;
    answer: string;
    selectOptions?: string[];
    radioOptions?: string[];
    radioOtherOptionEntry?: string;
    checkOptionAnswers?: CheckOptionAnswers[],
    confirmationTypeMsg?: string;
    confirmationStatus?: string;
    confirmationAnswer?: boolean;
    isRequired: boolean;
    dependsOnQuestionAnswer?: Map<number, string>;
    questionHeading?: string;
    questionViewable?: boolean;
    conditionalYesMsg?: string;
    conditionalNoMsg?: string;
    tooltip?: string;
    shouldDisplayNotFilledError?: boolean;
    errorMsgText?: string;
};

export enum QuestionTypes {
    Checkbox = "check",
    SelectBox = "select",
    RadioButton = "radio",
    ConfirmationToggle = "confirmation"
};

export enum QuestionConfirmationStatus {
    Confirmed = "Yes",
    Unconfirmed = "No"
};

export interface QuestionDependenceMapObj {
    key: number;
    value: string;
};

export interface QuestionsPayload {
    problemMenuConfig_RefId: number;
    questionNumber: number;
    question: string;
    questionType: string;
    options: string;
    isRequired: boolean;
    dependsOnMap: string;
    conditionalYesMsg: string;
    conditionalNoMsg: string;
    tooltip: string;
    errorMsgText: string;
};

export interface ImageFilePayload {
    file: File;
    event: any;
}

export interface ImageFile {
    file: File;
    source: string;
    hash: string;
    dataURL: string;
    progress: number;
    imageStatus: 'uploading' | 'success' | 'rejected';
};

export interface ImageFileUploadErrorMapping {
    hash: string;
    errorType: PhotoUploadErrorType;
}

export interface ImageFileStatusUpdate {
    hash: string;
    imageStatus: 'success' | 'rejected';
}

export interface PhoneType {
    phoneType: string;
    phoneNumber: string;
};

export interface ContactInfo {
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    phone: PhoneType[];
    notificationsRequested: boolean;
};

export interface DnvLatLng {
    lat: number;
    lng: number;
};

export interface DnvBounds {
    northWestBound: DnvLatLng;
    southEastBound: DnvLatLng;
};

export interface DnvMapInfo {
    latLng: DnvLatLng;
    mapSizeInPixel: string;
    mapBounds: DnvBounds;
};

export enum CurrentMapCodePath {
    Untouched = 0,
    MovingMarker = 1,
    ClickPlacement = 2
};

export enum SearchItemType {
    Unknown = 0,
    Address = 1,
    StreetName = 2,
    Park = 3,
    Intersection = 4
};

export enum ProgressionStep {
    ProblemSelection = 1,
    ProblemSelectionInfo = 1.3,
    ProblemSelectionError = 1.5,
    LocationSelection = 2,
    DetailsInput = 3,
    ContactDetailsInput = 4,
    ReviewPage = 5,
    SubmissionComplete = 6,
    SystemError = 7
};

export enum PhoneTypeValue {
    Null = 'null',
    Home = '1',
    Cell = '2',
    Work = '3',
    Other = '4'
};

export interface PhoneTypeValueEntries {
    typeName: string;
    phoneTypeValue: PhoneTypeValue;
}

export enum PhotoUploadErrorType {
    UploadLimitMet = 1,
    PhotoUploadRejected = 2,
    IncorrectFileType = 3,
    TooManyPhotosSelected = 4,
    FileSizeTooBig = 5,
    ImageDimensionsTooSmall = 6,
    ClearErrorText = 7
};

export enum DaysOfTheWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
};

export interface SearchItem {
    id: number;
    name: string;
    type: SearchItemType;
    assetId?: string;
};

export interface SearchLocation {
    locationType: string;
    location: string;
};

export interface LocationCoordinate {
    ltopid: string;
    dblink: string;
    lat: number;
    lng: number;
};

export interface SearchCoordinatesPayload {
    id: number;
    name: string;
    type: SearchItemType;
    searchLocation?: SearchLocation[];
    assetId?: string;
    updatedBy?: string;
    success?: LocationCoordinate;
    error?: HttpErrorResponse;
};

export interface PublicProblem {
    problemType_RefId: number;
    group_RefId: number;
    description: string;
    reported_Date: Date;
    location_Description: string;
    location_Longitude: number;
    location_Latitude: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumbers: PhoneType[];
};

export interface ApplicationState {
    newProblemMenuItems: ProblemMenuItem[];
    unModifiedMenuItems: ProblemMenuItem[];
    filteredProblemMenuItemsCount: number;
    selectedProblemMenuItem: ProblemMenuSelection;
    problemLocation: string;
    problemLocationOrigin: string;
    furtherDetails: string;
    currentStep: number;
    problemDetails: string;
    problemPhotos: ImageFile[];
    contactInfo: ContactInfo;
    selectedLatLng: DnvLatLng | null;
    newProblemUniqueId: string;
    problemQuestions: QuestionAnswer[];
};

export const initialApplicationState: ApplicationState = {
    newProblemMenuItems: [],
    unModifiedMenuItems: [],
    filteredProblemMenuItemsCount: 0,
    selectedProblemMenuItem: {
        ProblemMenuConfig_RefId: 0,
        ProblemType_RefId: 0,
        Group_RefId: 0,
        MenuOne: '',
        MenuTwo: '',
        MenuThree: '',
        messageDisplay: '',
        selected: false,
        CanCreateCodeCase: false,
        AdditionalQuestions: [],
        IsSNation: false,
        IsTWNation: false
    },
    problemLocation: '',
    problemLocationOrigin: '',
    furtherDetails: '',
    currentStep: 1,
    problemDetails: '',
    problemPhotos: [],
    contactInfo: {
        firstName: '',
        lastName: '',
        address: '',
        email: '',
        phone: [],
        notificationsRequested: false
    },
    selectedLatLng: {
        lat: 0,
        lng: 0
    },
    newProblemUniqueId: '',
    problemQuestions: []
};

export interface SnowLayer {
    currentVersion: number;
    cimVersion: string;
    id: number;
    name: string;
    type: string;
    description: string;
    geometryType: string;
    sourceSpatialReference: SpatialReference;
    copyrightText: string;
    parentLayer: null | any; // Use appropriate type if known
    subLayers: any[]; // Use appropriate type if known
    minScale: number;
    maxScale: number;
    drawingInfo: DrawingInfo;
    defaultVisibility: boolean;
    extent: Extent;
    hasAttachments: boolean;
    htmlPopupType: string;
    displayField: string;
    typeIdField: string;
    subtypeFieldName: string;
    subtypeField: string;
    defaultSubtypeCode: number;
    fields: Field[];
    geometryField: any; // Use appropriate type if known
    indexes: Index[];
    types: LayerType[];
    subtypes: Subtype[];
    relationships: any[]; // Use appropriate type if known
    canModifyLayer: boolean;
    canScaleSymbols: boolean;
    hasLabels: boolean;
    capabilities: string;
    maxRecordCount: number;
    supportsStatistics: boolean;
    supportsExceedsLimitStatistics: boolean;
    supportsAdvancedQueries: boolean;
    supportedQueryFormats: string;
    isDataVersioned: boolean;
    ownershipBasedAccessControlForFeatures: OwnershipAccess;
    useStandardizedQueries: boolean;
    supportedSpatialRelationships: string[];
    advancedQueryCapabilities: AdvancedQueryCapabilities;
    supportsDatumTransformation: boolean;
    advancedQueryAnalyticCapabilities: AdvancedQueryAnalyticCapabilities;
    dateFieldsTimeReference: null | any; // Use appropriate type if known
    preferredTimeReference: null | any; // Use appropriate type if known
    datesInUnknownTimezone: boolean;
    hasGeometryProperties: boolean;
    geometryProperties: GeometryProperties;
    hasMetadata: boolean;
    isDataArchived: boolean;
    archivingInfo: ArchivingInfo;
    supportsCoordinatesQuantization: boolean;
    supportsDynamicLegends: boolean;
    serviceItemId: string;
}

interface SpatialReference {
    wkid: number;
    latestWkid: number;
    xyTolerance: number;
    zTolerance: number;
    mTolerance: number;
    falseX: number;
    falseY: number;
    xyUnits: number;
    falseZ: number;
    zUnits: number;
    falseM: number;
    mUnits: number;
}

interface DrawingInfo {
    renderer: Renderer;
    scaleSymbols: boolean;
    transparency: number;
    labelingInfo: null | any; // Use appropriate type if known
}

interface Renderer {
    type: string;
    field1: string;
    uniqueValueInfos: UniqueValueInfo[];
    fieldDelimiter: string;
    authoringInfo: AuthoringInfo;
}

export interface UniqueValueInfo {
    symbol: Symbol;
    value: string;
    label: string;
}

interface Symbol {
    type: string;
    style: string;
    color: number[];
    width: number;
}

interface AuthoringInfo {
    colorRamp: ColorRamp;
}

interface ColorRamp {
    type: string;
    colorRamps: ColorRampItem[];
}

interface ColorRampItem {
    type: string;
    algorithm: string;
    fromColor: number[];
    toColor: number[];
}

interface Extent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference: SpatialReference;
}

interface Field {
    name: string;
    type: string;
    alias: string;
    length?: number; // Optional, as some fields may not have a length
    domain?: Domain | null; // Optional, as some fields may not have a domain
}

interface Domain {
    type: string;
    name: string;
    description: string;
    codedValues: CodedValue[];
    mergePolicy?: string;
    splitPolicy?: string;
}

interface CodedValue {
    name: string;
    code: number;
}

interface Index {
    name: string;
    fields: string;
    isAscending: boolean;
    isUnique: boolean;
    description: string;
}

interface LayerType {
    domains: { [key: string]: DomainType };
    id: number;
    name: string;
}

interface DomainType {
    type: string;
}

interface Subtype {
    domains: { [key: string]: DomainType };
    defaultValues: { [key: string]: any }; // Use appropriate types based on expected values
    code: number;
    name: string;
}

interface OwnershipAccess {
    allowOthersToQuery: boolean;
}

interface AdvancedQueryCapabilities {
    useStandardizedQueries: boolean;
    supportsStatistics: boolean;
    supportsPercentileStatistics: boolean;
    supportsHavingClause: boolean;
    supportsOrderBy: boolean;
    supportsDistinct: boolean;
    supportsCountDistinct: boolean;
    supportsPagination: boolean;
    supportsLod: boolean;
    supportsQueryWithLodSR: boolean;
    supportsTrueCurve: boolean;
    supportsQueryWithDatumTransformation: boolean;
    supportsReturningQueryExtent: boolean;
    supportsQueryWithDistance: boolean;
    supportsSqlExpression: boolean;
    supportsTimeRelation: boolean;
    supportsSqlFormat: boolean;
    supportsQueryAnalytic: boolean;
}

interface AdvancedQueryAnalyticCapabilities {
    supportsLinearRegression: boolean;
    supportsAsync: boolean;
    supportsPercentileAnalytic: boolean;
}

interface GeometryProperties {
    shapeLengthFieldName: string;
    units: string;
    mapUnits: { uwkid: number };
}

interface ArchivingInfo {
    supportsQueryWithHistoricMoment: boolean;
    startArchivingMoment: number;
}
